import React, { useContext, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, Camera, Download, Globe2, LoaderCircle, MapPin, Save, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { GlobalState } from "../../data/Context";
import { buildAssetUrl, clearStoredSession, setStoredSession } from "../../data/authStorage";
import {
  deleteOrganizationAccount,
  exportOrganizationData,
  getOrganizationProfile,
  requestOrganizationDeletionCode,
  updateOrganizationProfile,
} from "../../api/organizationProfile";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import ChangePasswordPanel from "../../components/profile/ChangePasswordPanel";

const emptyForm = {
  name: "", phone: "", website: "", industry: "", companySize: "",
  description: "", address: "", city: "", state: "", country: "Nigeria",
  timezone: "Africa/Lagos", dateFormat: "DD/MM/YYYY", weekStartsOn: "Monday",
};

const fieldClass = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#1234CD] focus:ring-2 focus:ring-[#1234CD]/10";
const companySizeOptions = ["1-10", "11-25", "26-50", "51-100", "101-200", "201-500", "501-1000", "1001-4999", "5000+"];

const Field = ({ label, children, ...props }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    {children || <input className={fieldClass} {...props} />}
  </label>
);

export default function OrganizationProfile() {
  const navigate = useNavigate();
  const { organization, setOrganization } = useContext(GlobalState);
  const [form, setForm] = useState(emptyForm);
  const [profile, setProfile] = useState(organization || null);
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [deletionError, setDeletionError] = useState("");
  const [deletionMessage, setDeletionMessage] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    getOrganizationProfile()
      .then(({ organization: result }) => {
        if (!mounted) return;
        setProfile(result);
        setForm({ ...emptyForm, ...result });
      })
      .catch((requestError) => {
        if (mounted) setError(requestError?.response?.data?.message || "Unable to load organization profile.");
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const logo = useMemo(() => preview || buildAssetUrl(profile?.logoUrl || profile?.logo || profile?.image), [preview, profile]);
  const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const selectLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select an image file."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("Organization profile picture is too large. Please choose an image that is 10 MB or smaller."); return; }
    if (preview) URL.revokeObjectURL(preview);
    setLogoFile(file);
    setPreview(URL.createObjectURL(file));
    setError("");
  };

  const submit = async () => {
    try {
      setSaving(true); setError(""); setMessage("");
      const { organization: updated } = await updateOrganizationProfile(form, logoFile);
      setProfile(updated); setForm({ ...emptyForm, ...updated }); setLogoFile(null); setPreview("");
      setOrganization(updated); setStoredSession({ organization: updated });
      setMessage("Organization profile updated successfully.");
    } catch (requestError) {
      const detail = requestError?.response?.data?.message || "Unable to update organization profile.";
      setError(Array.isArray(detail) ? detail.join(", ") : detail);
    } finally { setSaving(false); }
  };

  const sendDeletionCode = async () => {
    try {
      setSendingCode(true);
      setDeletionError("");
      const result = await requestOrganizationDeletionCode();
      setCodeSent(true);
      setDeletionMessage(result?.message || "Confirmation code sent. It expires in 10 minutes.");
    } catch (requestError) {
      const detail = requestError?.response?.data?.message || "Unable to send a confirmation code.";
      setDeletionError(Array.isArray(detail) ? detail.join(", ") : detail);
    } finally {
      setSendingCode(false);
    }
  };

  const confirmDeletion = async () => {
    if (!/^\d{6}$/.test(confirmationCode)) {
      setDeletionError("Enter the 6-digit confirmation code sent to the organization email.");
      return;
    }
    try {
      setDeleting(true);
      setDeletionError("");
      await deleteOrganizationAccount(confirmationCode);
      clearStoredSession();
      setOrganization(null);
      navigate("/login", { replace: true });
    } catch (requestError) {
      const detail = requestError?.response?.data?.message || "Unable to delete the organization account.";
      setDeletionError(Array.isArray(detail) ? detail.join(", ") : detail);
    } finally {
      setDeleting(false);
    }
  };

  const downloadDataExport = async () => {
    try {
      setExporting(true);
      setDeletionError("");
      const bundle = await exportOrganizationData();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cehr-${(profile?.name || "organization").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-data-export.json`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDeletionMessage("Your export is ready. It includes employee records, payroll, timesheets, attendance, and document links.");
    } catch (requestError) {
      const detail = requestError?.response?.data?.message || "Unable to prepare the organization data export.";
      setDeletionError(Array.isArray(detail) ? detail.join(", ") : detail);
    } finally {
      setExporting(false);
    }
  };

  const closeDeleteDialog = () => {
    if (sendingCode || deleting) return;
    setDeleteOpen(false);
    setCodeSent(false);
    setConfirmationCode("");
    setDeletionError("");
    setDeletionMessage("");
  };

  if (loading) return <FetchLoadingState message="Loading organization profile..." className="min-h-[300px]" />;

  return (
    <div className="p-4 font-Outfit sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#400167]">Organization Profile</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your company identity, contact details and regional preferences.</p>
      </div>

      <section className="mb-6 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
        <div className="h-24 bg-gradient-to-r from-[#071C50] via-[#1234CD] to-[#6A35C9]" />
        <div className="px-5 pb-5 sm:px-7">
          <div className="-mt-12 flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl border-4 border-white bg-[#EEF2FF] shadow-md">
              <img src={logo || createInitialsAvatar(form.name || organization?.name)} alt={logo ? `${form.name} logo` : "Organization profile"} className="h-full w-full object-cover" />
            </div>
            <div className="flex-1 pb-1">
              <h2 className="text-xl font-semibold text-[#071C50]">{form.name || "Your organization"}</h2>
              <p className="text-sm text-slate-500">{profile?.email}</p>
            </div>
            <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#1234CD] px-4 py-2.5 text-sm font-semibold text-[#1234CD] hover:bg-[#F4F7FF]">
              <Camera size={17} /> {logoFile ? "Logo selected" : "Change company logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="sr-only" onChange={selectLogo} />
            </label>
          </div>
          <p className="mt-3 text-xs text-slate-400">PNG, JPG, WebP or SVG. Maximum 10 MB. A square image works best.</p>
        </div>
      </section>

      {(error || message) && <div role="status" className={`mb-5 rounded-xl border px-4 py-3 text-sm ${error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{error || message}</div>}

      <div className="grid gap-6 xl:grid-cols-3">
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-5 flex items-center gap-2"><Building2 size={19} className="text-[#1234CD]" /><h3 className="font-semibold text-[#071C50]">Company details</h3></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Organization name" value={form.name} onChange={change("name")} required />
            <Field label="Industry" value={form.industry} onChange={change("industry")} placeholder="e.g. Technology" />
            <Field label="Company size"><select className={fieldClass} value={form.companySize} onChange={change("companySize")}><option value="">Select size</option>{form.companySize && !companySizeOptions.includes(form.companySize) ? <option value={form.companySize}>{form.companySize} (existing)</option> : null}{companySizeOptions.map((size) => <option key={size}>{size}</option>)}</select></Field>
            <Field label="Website" type="url" value={form.website} onChange={change("website")} placeholder="https://example.com" />
            <Field label="Phone number" type="tel" value={form.phone} onChange={change("phone")} />
            <Field label="Account email" value={profile?.email || ""} disabled />
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2">About the organization<textarea className={`${fieldClass} min-h-28 resize-y`} value={form.description} onChange={change("description")} maxLength={1000} placeholder="A short description of your organization" /></label>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center gap-2"><Globe2 size={19} className="text-[#1234CD]" /><h3 className="font-semibold text-[#071C50]">Regional settings</h3></div>
          <div className="space-y-4">
            <Field label="Timezone"><select className={fieldClass} value={form.timezone} onChange={change("timezone")}><option>Africa/Lagos</option><option>UTC</option><option>Europe/London</option><option>America/New_York</option><option>Asia/Dubai</option></select></Field>
            <Field label="Date format"><select className={fieldClass} value={form.dateFormat} onChange={change("dateFormat")}><option>DD/MM/YYYY</option><option>MM/DD/YYYY</option><option>YYYY-MM-DD</option></select></Field>
            <Field label="Week starts on"><select className={fieldClass} value={form.weekStartsOn} onChange={change("weekStartsOn")}><option>Monday</option><option>Sunday</option><option>Saturday</option></select></Field>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm xl:col-span-3">
          <div className="mb-5 flex items-center gap-2"><MapPin size={19} className="text-[#1234CD]" /><h3 className="font-semibold text-[#071C50]">Registered address</h3></div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2 lg:col-span-4">Street address<input className={fieldClass} value={form.address} onChange={change("address")} /></label>
            <Field label="City" value={form.city} onChange={change("city")} />
            <Field label="State / Province" value={form.state} onChange={change("state")} />
            <Field label="Country" value={form.country} onChange={change("country")} />
          </div>
        </section>
      </div>

      <ChangePasswordPanel className="mt-6" />

      <div className="mt-6 flex flex-col-reverse justify-between gap-3 sm:flex-row sm:items-center">
        <button type="button" onClick={() => setDeleteOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-300 px-5 py-3 text-sm font-semibold text-red-700 hover:bg-red-50">
          <Trash2 size={17} /> Archive organization account
        </button>
        <button type="button" onClick={submit} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1234CD] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#0E2AA7] disabled:opacity-60"><Save size={17} />{saving ? "Saving changes..." : "Save changes"}</button>
      </div>

      {deleteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-organization-title">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-red-100 bg-red-50 p-5">
              <div className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700"><AlertTriangle size={22} /></span>
                <div>
                  <h2 id="delete-organization-title" className="text-lg font-bold text-red-900">Archive organization account?</h2>
                  <p className="mt-1 text-sm font-medium text-red-700">The organization will be retained for 3 months before permanent deletion.</p>
                </div>
              </div>
              <button type="button" onClick={closeDeleteDialog} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-white"><X size={19} /></button>
            </div>
            <div className="space-y-5 p-5">
              <div className="rounded-xl border border-red-200 bg-red-50/60 p-4 text-sm leading-6 text-red-900">
                Archiving <strong>{profile?.name || "this organization"}</strong> immediately blocks sign-in and workspace access for every organization user. The workspace and its data will be retained for 3 months, then permanently purged. A Super Admin can restore it during the archive period; an active legal hold pauses the purge.
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-950">
                <strong>Download a copy first.</strong> The export contains employee records, payroll runs and compensation, timesheets, attendance, and document metadata with file links.
                <button type="button" onClick={downloadDataExport} disabled={exporting || deleting} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-blue-300 bg-white px-4 py-2.5 font-semibold text-blue-800 disabled:opacity-60">
                  {exporting ? <LoaderCircle size={16} className="animate-spin" /> : <Download size={16} />}
                  {exporting ? "Preparing export..." : "Download organization data"}
                </button>
              </div>
              <p className="text-xs leading-5 text-slate-500">Individual employee privacy and deletion requests are handled separately and are not affected by this organization account request.</p>

              {!codeSent ? (
                <div>
                  <p className="text-sm text-slate-600">To continue, request a one-time confirmation code. It will be sent only to <strong>{profile?.email}</strong>.</p>
                  <button type="button" onClick={sendDeletionCode} disabled={sendingCode} className="mt-4 w-full rounded-xl bg-red-700 px-4 py-3 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60">
                    {sendingCode ? "Sending confirmation code..." : "Send confirmation code"}
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-slate-800">
                    Enter the 6-digit confirmation code
                    <input
                      value={confirmationCode}
                      onChange={(event) => setConfirmationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={6}
                      placeholder="000000"
                      className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl font-bold tracking-[0.4em] outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                    />
                  </label>
                  <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row">
                    <button type="button" onClick={sendDeletionCode} disabled={sendingCode || deleting} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                      {sendingCode ? "Sending..." : "Resend code"}
                    </button>
                    <button type="button" onClick={confirmDeletion} disabled={deleting || confirmationCode.length !== 6} className="flex-1 rounded-xl bg-red-700 px-4 py-3 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60">
                    {deleting ? "Archiving..." : "Archive organization"}
                    </button>
                  </div>
                </div>
              )}

              {(deletionError || deletionMessage) ? (
                <p role="status" className={`rounded-lg px-3 py-2 text-sm ${deletionError ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                  {deletionError || deletionMessage}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
